import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cva } from 'class-variance-authority';
import {
  KoobSettingsBody,
  KoobSidebar,
  KoobNavbarUserDropdown,
  KoobOrganizationPicker,
  KoobOrganizationRequiredPrompt,
  useInSettingsModal,
  useKoobSidebarCollapsed,
} from '@koob/ui';
import {
  closeTripWizard,
  openTripWizard,
  useTripWizardOpen,
} from '@koob/ui/src/features/tripWizard';
import DesignTripFolderModal from '../../containers/TripDesigner/DesignTrip/DesignTripFolderModal';
import { ActiveFolderCard } from '@koob/booking';
import {
  getFolderStatus,
  ORGANIZATION_REQUIRED_EVENT,
  switchActingOrganization,
} from '@koob/utils';
import { useTranslation } from 'react-i18next';
import { useTripFilters } from '@koob/trip-designer';
import { useApp, useAuth, useBooking, useExperienceBooking } from '../../hooks';
import { useActiveFolder, useKoobStore } from 'store';
import { useAsync } from 'react-async';
import { getRequestAccess } from 'api/toRequestAccess';
import { revokeToken } from 'api/auth';
import { KoobStateEnum } from 'utils';
import { useEffect, useRef, useState } from 'react';
import KoobNavbarTripDesigner from './KoobNavbarTripDesigner';
import NotificationsDropdown from '../NotificationsDropdown';
import ExperiencesBasket from '../Experience/ExperiencesBasket';
import BrowseFoldersModal from './BrowseFoldersModal';
import { settingsSections } from '../settingsSections';

// Map a snake_case backend role (e.g. 'to_admin') to the camelCase key used in
// the `user:roles` translation namespace (e.g. 'toAdmin').
const roleToTranslationKey = role =>
  role?.replace(/_([a-z])/g, (_, char) => char.toUpperCase());

export default function KoobLayout({
  children,
  horizontalPadding = true,
  header,
  variant,
}) {
  const {
    currentOrganization,
    currentOrganizationIdInt,
    user,
    currentUserRole,
    isAllowedTo,
  } = useApp();
  const { logout, impersonating, setImpersonationToken } = useAuth();
  const { t } = useTranslation([
    'mainSidebar',
    'tripDesigner',
    'impersonation',
  ]);
  const navigate = useNavigate();
  const { resetContext, setFilters } = useBooking();
  const {
    resetContext: resetExperienceContext,
    basket,
    setExperienceFilters,
  } = useExperienceBooking();
  const [sidebarCollapsed] = useKoobSidebarCollapsed();
  const inSettingsModal = useInSettingsModal();
  // The composer's trip header owns the top bar — hide the basket and bell.
  const onTripDesigner = useLocation().pathname.startsWith(
    '/trip-designer/compose',
  );
  const [basketOpen, setBasketOpen] = useState(false);
  const browseFoldersOpen = useKoobStore(s => s.browseFoldersOpen);
  const setBrowseFoldersOpen = useKoobStore(s => s.setBrowseFoldersOpen);
  const wizardOpen = useTripWizardOpen();

  const activeFolder = useActiveFolder();
  const tripOpen = Object.keys(useTripFilters() ?? {}).length > 1;
  const closeFolder = useKoobStore(s => s.closeFolder);
  const openFolder = useKoobStore(s => s.openFolder);

  const activeFolderId = activeFolder?.id;
  const previousFolderId = useRef(activeFolderId);
  useEffect(() => {
    if (previousFolderId.current && !activeFolderId) {
      setFilters(current => ({ ...current, rooms: undefined }));
      setExperienceFilters(current => ({
        ...current,
        experienceComposition: undefined,
      }));
    }
    previousFolderId.current = activeFolderId;
  }, [activeFolderId, setFilters, setExperienceFilters]);

  // Same flow as UserImpersonationAlert: revoke the impersonation token then
  // return to the real account, or log out if both tokens match.
  const stopImpersonating = async () => {
    if (
      localStorage.getItem('accessToken') ===
      localStorage.getItem('impersonationToken')
    ) {
      logout();
      return;
    }
    try {
      await revokeToken(true);
    } catch (error) {
      console.error(
        `An error occurred while revoking the token: ${error.message}`,
      );
    } finally {
      setImpersonationToken('');
      window.location.href = '/';
    }
  };

  const [isValidTripDesignerRequest, setIsValidTripDesignerRequest] = useState(
    localStorage.getItem('tripDesignerRequestState') ===
      KoobStateEnum.VALIDATED,
  );

  const { data } = useAsync({
    promiseFn: getRequestAccess,
  });

  useEffect(() => {
    if (!data) return;
    const requestAccess = data?.data;
    const isValid =
      requestAccess?.tripDesignerRequestState === KoobStateEnum.VALIDATED;
    const state = requestAccess?.tripDesignerRequestState ?? '';

    setIsValidTripDesignerRequest(isValid);
    localStorage.setItem('tripDesignerRequestState', state);
  }, [data]);

  const links = [
    {
      to: '/folders',
      icon: 'far fa-folder-user',
      label: t('folders'),
    },
    {
      // No page of its own: a label pinned open over Hotels and Experiences.
      icon: 'far fa-store',
      label: t('marketplace'),
      alwaysExpanded: true,
      dataCy: 'marketplace',
      children: [
        {
          to: '/hotels',
          label: t('hotels'),
          end: true,
          onClick: () => resetContext(),
        },
        {
          to: '/experiences',
          label: t('experiences'),
          end: true,
          onClick: () => resetExperienceContext(),
        },
      ],
    },
  ];

  const tripDesignerLink = {
    // KD-3723: the index route is the Discover / request-access page, which a
    // validated organization must never land on.
    to: isValidTripDesignerRequest ? '/trip-designer/trips' : '/trip-designer',
    icon: 'far fa-cube',
    label: t('tripDesigner'),
    dataCy: 'trip-designer',
  };

  if (isValidTripDesignerRequest) {
    // Trip Designer is pinned open: children always visible, no chevron.
    tripDesignerLink.alwaysExpanded = true;
    tripDesignerLink.children = [
      {
        // Opens over the current page instead of navigating away.
        onClick: openTripWizard,
        label: t('designTrip'),
        activeMatch: ['/trip-designer/compose'],
        disabled: tripOpen || !activeFolder,
        disabledTip: tripOpen
          ? t('tripDesigner:compose.tripAlreadyOpen')
          : activeFolder
            ? undefined
            : t('tripDesigner:compose.clientFolderRequired'),
        onDisabledClick: tripOpen
          ? undefined
          : () => setBrowseFoldersOpen(true, 'designTrip'),
      },
      {
        to: '/trip-designer/trips',
        label: t('dmcTemplates'),
      },
      {
        to: '/trip-designer/library',
        label: t('library'),
      },
    ];
  }

  links.push(tripDesignerLink);

  links.push({
    to: '/dmcs',
    icon: 'far fa-link',
    label: t('dmcs'),
  });

  // One link, last, opens the settings modal on its first section.
  const settingsLinks = settingsSections(
    t,
    isAllowedTo,
    currentUserRole,
  ).flatMap(group => group.links);
  links.push({
    to: settingsLinks[0].to,
    icon: 'far fa-gear',
    label: t('settingsModal.title'),
    activeMatch: settingsLinks.flatMap(link => link.activeMatch),
    dataCy: 'settings',
  });

  const userName = user?.firstName
    ? `${user.firstName} ${user.lastName || ''}`
    : user?.email;

  const organizationName =
    currentOrganization?.displayName || user?.organizationName;

  const roleKey = roleToTranslationKey(currentUserRole);
  const roleLabel = roleKey ? t(`roles.${roleKey}`, { ns: 'user' }) : undefined;

  // KD-3391: the trip designer variant owns the whole page padding (p-5) and
  // spaces the filter card / compose grid with gap-5.
  const container = cva([], {
    variants: {
      variant: {
        default: 'py-5',
        // Viewport-locked: 100vh minus the fixed 80px navbar and the shell's
        // pb-5 — the page itself never scrolls, the columns do.
        tripDesigner: 'flex h-[calc(100vh-6.25rem)] flex-col gap-5 p-5',
      },
      horizontalPadding: {
        true: '',
      },
    },
    compoundVariants: [
      { variant: 'default', horizontalPadding: true, class: 'px-6 lg:px-8' },
    ],
    defaultVariants: { variant: 'default' },
  });

  // In the settings modal: the page alone, the app's chrome stays beneath.
  if (inSettingsModal) {
    return (
      <>
        {header}
        <KoobSettingsBody>{children}</KoobSettingsBody>
      </>
    );
  }

  // Sticky toolbars stop below the fixed navbar (h-20).
  return (
    <div className="min-h-screen bg-gray-50 pb-5 flex flex-col [--koob-sticky-top:5rem]">
      <KoobSidebar
        sections={[{ title: null, links }]}
        bottomSlot={collapsed => (
          <ActiveFolderCard
            folder={activeFolder}
            status={getFolderStatus(activeFolder)}
            collapsed={collapsed}
            onOpen={() => navigate(`/folders/${activeFolder?.id}`)}
            onClose={closeFolder}
            onBrowse={() => setBrowseFoldersOpen(true)}
            onDropFolder={openFolder}
          />
        )}
        router={{ NavLink, useLocation }}
      />

      <div
        className={[
          'transition-all duration-300',
          sidebarCollapsed ? 'ml-16' : 'ml-72',
        ].join(' ')}
      >
        {/* Top bar: organization picker, trip designer, user, basket, notifications */}
        <div
          className={[
            'fixed top-0 right-0 z-sticky h-20 flex items-center justify-end gap-4 px-6 bg-white border-b border-border transition-all duration-300',
            sidebarCollapsed ? 'left-16' : 'left-72',
          ].join(' ')}
        >
          {/* The one organization picker (KD-3735): every screen follows it. */}
          <KoobOrganizationPicker
            className="w-56 shrink-0"
            organizations={user?.organizations ?? []}
            currentOrganizationId={currentOrganization?.organizationId}
            onChange={switchActingOrganization}
            label={t('organizationPicker')}
            groupLabel={name => t('allOrganizationsOf', { name })}
          />

          {/* KD-3391: on compose, the page portals its trip header
              (ComposeHeader) into this slot */}
          <div className="min-w-0 flex-1" id="koob-navbar-trip-designer-slot">
            <KoobNavbarTripDesigner />
          </div>

          <KoobNavbarUserDropdown
            name={userName}
            email={user?.email}
            organization={organizationName}
            role={roleLabel}
            impersonating={impersonating}
            profileLabel={t('myProfile')}
            organizationLabel={t('myOrganization')}
            travelDiaryContentLabel={t('travelDiaryContent')}
            travelDiaryThemeLabel={t('travelDiaryTheme', 'Travel Diary Theme')}
            auditLogLabel={t('auditLog')}
            supportLabel={t('userGuide')}
            logoutLabel={t('signOut')}
            impersonatingLabel={t('impersonation:status')}
            impersonatingBannerLabel={t('impersonation:actingAs')}
            stopImpersonatingLabel={t('impersonation:stop')}
            onStopImpersonating={stopImpersonating}
            onProfile={() => navigate('/user')}
            onOrganization={() =>
              navigate(`/organizations/${currentOrganizationIdInt}`)
            }
            onTravelDiaryContent={
              isAllowedTo('readTravelDiaryContent')
                ? () => navigate('/travel-diary-content-library')
                : undefined
            }
            onTravelDiaryTheme={
              currentUserRole === 'to_admin'
                ? () => navigate('/travel-diary-settings')
                : undefined
            }
            onAuditLog={
              isAllowedTo('readAuditLog')
                ? () => navigate('/audit-log')
                : undefined
            }
            onSupport={() =>
              window.open('https://support.koob.tech/en/', '_blank')
            }
            onLogout={logout}
          />

          {!onTripDesigner && (
            <button
              type="button"
              onClick={() => setBasketOpen(true)}
              className="relative inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-k-p transition hover:bg-gray-100 hover:text-k-h focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              aria-label="Basket"
            >
              <i
                className="far fa-cart-shopping text-xl"
                aria-hidden="true"
                data-testid="basket"
              />
              {basket.length > 0 && (
                <span className="absolute -right-1 -top-1 inline-flex h-[1.15rem] min-w-[1.15rem] items-center justify-center rounded-full border-2 border-solid border-white bg-orange-500 px-1 text-[0.65rem] font-semibold leading-none text-k-h">
                  {basket.length}
                </span>
              )}
            </button>
          )}

          {/* Notifications last, as in DMC. */}
          {!onTripDesigner && <NotificationsDropdown />}
        </div>

        <ExperiencesBasket open={basketOpen} setOpen={setBasketOpen} />

        <div className="mt-20">
          {/* The page's KoobPageHeader, full width under the top bar. */}
          {header}
          <main className={container({ horizontalPadding, variant })}>
            {children}
          </main>
        </div>
      </div>

      <BrowseFoldersModal
        open={browseFoldersOpen}
        setOpen={setBrowseFoldersOpen}
      />

      {/* One instance for the whole app: every entry point calls
          openTripWizard() and it lands over whatever page is showing. */}
      {wizardOpen && (
        <DesignTripFolderModal open={wizardOpen} close={closeTripWizard} />
      )}

      {/* One prompt for the app: a 400 ORGANIZATION_REQUIRED asks which
          organization the action is for and switches the picker (KD-3735). */}
      <KoobOrganizationRequiredPrompt
        organizations={user?.organizations ?? []}
        eventName={ORGANIZATION_REQUIRED_EVENT}
        onChoose={switchActingOrganization}
        title={t('misc:organizationRequiredTitle')}
        text={t('misc:organizationRequiredText')}
      />
    </div>
  );
}
